import React from "react";

export interface BrowseFilters {
  brands: { _id: string; name: string; count: number; selected: boolean }[];
  priceRange: { min: number; max: number; applied: { min: number | null; max: number | null } } | null;
  attributes: Record<string, { value: string; count: number; selected: boolean }[]>;
  delivery: {
    freeDelivery: { count: number; selected: boolean };
    nextDayDelivery: { count: number; selected: boolean };
  } | null;
  discountBuckets: { count: number; label: string; minDiscount: number }[];
  ratingBuckets: { count: number; label: string; minRating?: number }[];
  condition: { value: string; count: number; selected: boolean }[];
}

export interface BrowseChild {
  name: string;
  count: number;
}

export interface ActiveFilters {
  parentCategory: string;
  subCategory: string;
  childCategory: string;
  brandId: string;
  minPrice?: number;
  maxPrice?: number;
  minDiscount?: number;
  freeDelivery?: boolean;
  nextDayDelivery?: boolean;
  condition?: string;
}

interface CategoriListFilterProps {
  // Browse API mode (Categories page)
  browseFilters?: BrowseFilters;
  browseChildren?: BrowseChild[];
  // Legacy Redux mode (productlist page) — kept for backwards compatibility
  superCategory?: string;
  parentCategories?: any[];
  subCategories?: any[];
  childCategories?: any[];
  brandMappings?: any[];
  filters: ActiveFilters & Record<string, any>;
  onFilterChange: (newFilters: Partial<ActiveFilters> & Record<string, any>) => void;
}

const FilterSection: React.FC<{
  id: string;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ id, title, defaultOpen = true, children }) => (
  <div className="border-bottom py-3">
    <button
      className="btn p-0 w-100 d-flex justify-content-between align-items-center bg-transparent border-0 shadow-none"
      data-bs-toggle="collapse"
      data-bs-target={`#${id}`}
      aria-expanded={defaultOpen ? "true" : "false"}
      aria-controls={id}
      type="button"
    >
      <b className="text-dark" style={{ fontSize: "14px", letterSpacing: "0.3px", fontWeight: 1000 }}>
        {title}
      </b>
      <i className="bi bi-chevron-down text-dark" style={{ fontSize: "12px", transition: "transform 0.2s" }}></i>
    </button>
    <div id={id} className={`collapse${defaultOpen ? " show" : ""}`}>
      <div className="pt-2">{children}</div>
    </div>
  </div>
);

const CheckItem: React.FC<{
  id: string;
  label: string;
  count?: number;
  checked: boolean;
  onChange: () => void;
}> = ({ id, label, count, checked, onChange }) => (
  <div className="form-check mb-2">
    <input
      className="form-check-input"
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
    />
    <label className="form-check-label text-black" htmlFor={id} style={{ fontSize: "13px", cursor: "pointer" }}>
      <span className="text-black font-weight-bold">{label}</span>
      {count !== undefined && <span className="text-black-50 fw-bold text-xs">({count})</span>}
    </label>
  </div>
);

const CategoriListFilter: React.FC<CategoriListFilterProps> = ({
  browseFilters,
  browseChildren,
  filters,
  onFilterChange,
}) => {
  const hasAnyFilter =
    filters.brandId ||
    filters.minPrice !== undefined ||
    filters.maxPrice !== undefined ||
    filters.minDiscount !== undefined ||
    filters.freeDelivery ||
    filters.nextDayDelivery ||
    filters.condition ||
    filters.childCategory;

  return (
    <div style={{ maxHeight: "calc(100vh - 100px)", overflowY: "auto", paddingRight: "4px" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
        <b className="text-dark fw-bold" style={{ fontSize: "16px" }}>Filters</b>
        {hasAnyFilter && (
          <button
            className="btn btn-link p-0 text-decoration-none"
            style={{ fontSize: "13px", color: "#ff3f6c" }}
            onClick={() =>
              onFilterChange({
                childCategory: "",
                brandId: "",
                minPrice: undefined,
                maxPrice: undefined,
                minDiscount: undefined,
                freeDelivery: undefined,
                nextDayDelivery: undefined,
                condition: undefined,
              })
            }
          >
            Clear All
          </button>
        )}
      </div>

      {/* Child Categories */}
      {browseChildren && browseChildren.length > 0 && (
        <FilterSection id="filterChildren" title="Category" defaultOpen>
          {browseChildren.map((child) => (
            <CheckItem
              key={child.name}
              id={`child-${child.name}`}
              label={child.name}
              count={child.count}
              checked={filters.childCategory === child.name}
              onChange={() =>
                onFilterChange({ childCategory: filters.childCategory === child.name ? "" : child.name })
              }
            />
          ))}
        </FilterSection>
      )}

      {/* Brands */}
      {browseFilters?.brands && browseFilters.brands.length > 0 && (
        <FilterSection id="filterBrand" title="Brand" defaultOpen>
          {browseFilters.brands.map((brand) => (
            <CheckItem
              key={brand._id}
              id={`brand-${brand._id}`}
              label={brand.name}
              count={brand.count}
              checked={filters.brandId === brand._id}
              onChange={() =>
                onFilterChange({ brandId: filters.brandId === brand._id ? "" : brand._id })
              }
            />
          ))}
        </FilterSection>
      )}

      {/* Price Range */}
      {browseFilters?.priceRange && (
        <FilterSection id="filterPrice" title="Price" defaultOpen>
          <div className="px-1">
            <div className="d-flex justify-content-between mb-2" style={{ fontSize: "12px", color: "#555" }}>
              <span>₹{browseFilters.priceRange.min.toLocaleString()}</span>
              <span>₹{browseFilters.priceRange.max.toLocaleString()}</span>
            </div>
            <div className="d-flex gap-2">
              <input
                type="number"
                className="form-control form-control-sm"
                placeholder="Min"
                value={filters.minPrice ?? ""}
                min={browseFilters.priceRange.min}
                max={browseFilters.priceRange.max}
                onChange={(e) => onFilterChange({ minPrice: e.target.value ? Number(e.target.value) : undefined })}
              />
              <input
                type="number"
                className="form-control form-control-sm"
                placeholder="Max"
                value={filters.maxPrice ?? ""}
                min={browseFilters.priceRange.min}
                max={browseFilters.priceRange.max}
                onChange={(e) => onFilterChange({ maxPrice: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
          </div>
        </FilterSection>
      )}

      {/* Discount */}
      {browseFilters?.discountBuckets && browseFilters.discountBuckets.length > 0 && (
        <FilterSection id="filterDiscount" title="Discount" defaultOpen={false}>
          {browseFilters.discountBuckets.map((bucket) => (
            <CheckItem
              key={bucket.minDiscount}
              id={`discount-${bucket.minDiscount}`}
              label={bucket.label}
              count={bucket.count}
              checked={filters.minDiscount === bucket.minDiscount}
              onChange={() =>
                onFilterChange({ minDiscount: filters.minDiscount === bucket.minDiscount ? undefined : bucket.minDiscount })
              }
            />
          ))}
        </FilterSection>
      )}

      {/* Delivery */}
      {browseFilters?.delivery && (
        <FilterSection id="filterDelivery" title="Delivery" defaultOpen={false}>
          {browseFilters.delivery.freeDelivery.count > 0 && (
            <CheckItem
              id="delivery-free"
              label="Free Delivery"
              count={browseFilters.delivery.freeDelivery.count}
              checked={!!filters.freeDelivery}
              onChange={() => onFilterChange({ freeDelivery: filters.freeDelivery ? undefined : true })}
            />
          )}
          {browseFilters.delivery.nextDayDelivery.count > 0 && (
            <CheckItem
              id="delivery-next"
              label="Next Day Delivery"
              count={browseFilters.delivery.nextDayDelivery.count}
              checked={!!filters.nextDayDelivery}
              onChange={() => onFilterChange({ nextDayDelivery: filters.nextDayDelivery ? undefined : true })}
            />
          )}
        </FilterSection>
      )}

      {/* Condition */}
      {browseFilters?.condition && browseFilters.condition.length > 0 && (
        <FilterSection id="filterCondition" title="Condition" defaultOpen={false}>
          {browseFilters.condition.map((item) => (
            <CheckItem
              key={item.value}
              id={`condition-${item.value}`}
              label={item.value.charAt(0).toUpperCase() + item.value.slice(1)}
              count={item.count}
              checked={filters.condition === item.value}
              onChange={() =>
                onFilterChange({ condition: filters.condition === item.value ? undefined : item.value })
              }
            />
          ))}
        </FilterSection>
      )}

      {/* Dynamic Attributes */}
      {browseFilters?.attributes &&
        Object.entries(browseFilters.attributes).map(([attrName, values]) =>
          values.length > 0 ? (
            <FilterSection key={attrName} id={`filterAttr-${attrName}`} title={attrName} defaultOpen={false}>
              {values.map((attr) => (
                <CheckItem
                  key={attr.value}
                  id={`attr-${attrName}-${attr.value}`}
                  label={attr.value}
                  count={attr.count}
                  checked={false}
                  onChange={() => {}}
                />
              ))}
            </FilterSection>
          ) : null
        )}

      <style>{`
        .form-check-input:checked {
          background-color: #ff3f6c;
          border-color: #ff3f6c;
        }
        .form-check-input:focus {
          border-color: #ff3f6c;
          box-shadow: 0 0 0 0.2rem rgba(255,63,108,0.15);
        }
        .btn[aria-expanded="false"] .bi-chevron-down {
          transform: rotate(-90deg);
        }
      `}</style>
    </div>
  );
};

export default CategoriListFilter;
